import { notFound } from '@tanstack/react-router'
import {
	type EntityKey,
	type EntityResourceConfig,
	getEntityConfig,
	isEntityKey,
	type SettingsConfig,
} from '../generated/entity-config'

export type DerivedTab =
	| { key: 'overview'; kind: 'overview' }
	| { key: string; kind: 'relation'; relationKey: string }
	| { key: 'settings'; kind: 'settings' }

export function resolveEntity(entity: string): EntityResourceConfig<EntityKey> {
	if (!isEntityKey(entity)) throw notFound()
	const Entity = getEntityConfig(entity)
	if (Entity.kind !== 'entity') throw notFound()
	const resolvedEntity: EntityResourceConfig<EntityKey> = Entity
	return resolvedEntity
}

export function parseEntityId(entityId: string): number {
	const value = Number(entityId)
	if (!Number.isFinite(value)) throw notFound()
	return value
}

export function getEntityTabs(
	Entity: EntityResourceConfig<EntityKey>,
): DerivedTab[] {
	const tabs: DerivedTab[] = [{ key: 'overview', kind: 'overview' }]
	for (const relationKey of Object.keys(Entity.relations ?? {})) {
		tabs.push({ key: relationKey, kind: 'relation', relationKey })
	}
	if (Entity.settings) tabs.push({ key: 'settings', kind: 'settings' })
	return tabs
}

export function getEntityDefaultTab(
	Entity: EntityResourceConfig<EntityKey>,
): string {
	return getEntityTabs(Entity)[0]?.key ?? 'overview'
}

export function resolveEntityTab(
	Entity: EntityResourceConfig<EntityKey>,
	tab: string,
): DerivedTab {
	const normalizedTab = tab === 'detail' || tab === 'details' ? 'overview' : tab
	const found = getEntityTabs(Entity).find((item) => item.key === normalizedTab)
	if (!found) throw notFound()
	return found
}

function getSettingsSectionCandidates(section?: string) {
	if (!section) return []
	const withoutSettingsSuffix = section.endsWith('Settings')
		? section.slice(0, -'Settings'.length)
		: section
	const camelCasedSlug = withoutSettingsSuffix.replace(
		/-([a-z])/g,
		(_match, value: string) => value.toUpperCase(),
	)
	return [section, withoutSettingsSuffix, camelCasedSlug]
}

export function resolveSettingsSection(
	settings: SettingsConfig,
	section?: string,
) {
	const key =
		getSettingsSectionCandidates(section).find(
			(candidate) => settings.sections[candidate],
		) ??
		settings.defaultSection ??
		Object.keys(settings.sections)[0]
	const value = settings.sections[key]
	if (!value) throw notFound()
	return value
}
